import {
    isFeatureEnabled,
    toFeatureState,
} from '@etrigan/feature-toggles-client'
import {
    Block,
    CardEvents,
    CollectionEvent,
    LinkClickedEvent,
    PlayerEvent,
    useToggleState,
    VideoDetails,
} from '@news-mono/web-common'
import React from 'react'
import { Logger } from 'typescript-log'
import { Blog } from '../../content/Blog/Blog'
import { InlineRelatedContent } from '../../content/InlineRelatedContent/InlineRelatedContent'
import {
    GetVideoQueue,
    VideoOptions,
} from '../../content/Video/PlayerInterface'
import { useGdprMode } from '../../content/Video/utils/gdpr-mode'
import { TwinImageWrapper } from '../../publication/ArticleBlockContent/ArticleBlockContent.styled'
import { ArticleImage } from '../../publication/ArticleImage/ArticleImage'
import { ArticleVideo } from '../../publication/ArticleVideo/ArticleVideo'
import { Embed } from '../../publication/Embed/Embed'
import { Paragraph } from '../../publication/Paragraph/Paragraph'
import { Pullquote } from '../../publication/Pullquote/Pullquote'
import { VideoQueue } from '../../templates/Publication/lib/get-video-queue'
import { List } from '../../typography/List/List'
import { Subhead } from '../../typography/Subhead/Subhead'

export function ArticleBlock({
    block,
    onEvent,
    log,
    adUnitPath,
    getVideoQueue,
    showInlineVideoTitles,
    publicationPathname,
}: {
    block: Block
    onEvent: (
        event: PlayerEvent | LinkClickedEvent | CardEvents | CollectionEvent,
    ) => void
    log: Logger
    adUnitPath: string
    getVideoQueue: GetVideoQueue | undefined
    showInlineVideoTitles: boolean | undefined
    publicationPathname: string | undefined
}) {
    const toggles = useToggleState()
    const gdprMode = useGdprMode()

    switch (block.kind) {
        case 'text':
            return (
                <Paragraph
                    intentions={block.intentions}
                    text={block.text}
                    onEvent={onEvent}
                />
            )
        case 'pull-quote':
            return <Pullquote {...block} />
        case 'embed':
            return (
                <Embed
                    log={log}
                    onEvent={onEvent}
                    adUnitPath={adUnitPath}
                    {...block}
                />
            )
        case 'blog':
            return (
                <Blog
                    blogId={block.blogId}
                    provider={block.provider}
                    blogState={block.blogState}
                />
            )
        case 'twin-image': {
            return (
                // 'twin-image-wrapper' utility class required to fix a cascade issue outlined in SWM-4447
                <TwinImageWrapper className="twin-image-wrapper">
                    <ArticleImage
                        align={'left'}
                        isTwin
                        key="1"
                        image={block.left}
                        figCaption={{
                            byline: block.left.byline,
                            captionText: block.left.captionText,
                            credit: block.left.credit,
                            kind: 'image',
                        }}
                    />
                    <ArticleImage
                        align={'right'}
                        isTwin
                        key="2"
                        image={block.right}
                        figCaption={{
                            byline: block.right.byline,
                            captionText: block.right.captionText,
                            credit: block.right.credit,
                            kind: 'image',
                        }}
                    />
                </TwinImageWrapper>
            )
        }
        case 'inline': {
            switch (block.inlineKind) {
                case 'video': {
                    const heading = block.inlineDetails.captionText || ''
                    const posterImage = block.inlineVideo.posterImage
                    const videoId = block.inlineVideo.id
                    const accountId = block.inlineVideo.accountId
                    const videoType = block.inlineVideo.videoType
                    const videoDetails: VideoDetails = {
                        id: videoId,
                        accountId,
                        heading,
                        posterImage,
                        videoType,
                    }
                    const captionText = block.inlineDetails.captionText

                    const videoOptions: VideoOptions = {
                        adUnitPath,
                        canAutoplayVideos: false,
                        enableSnowplow: isFeatureEnabled(
                            toFeatureState(toggles),
                            'snowplow',
                        ),
                        enablePrebid: isFeatureEnabled(
                            toFeatureState(toggles),
                            'prebid-video',
                        ),
                        enableNielsenVideoReporting: isFeatureEnabled(
                            toFeatureState(toggles),
                            'nielsen-video-events',
                        ),
                        enableNielsenSubbrandReporting: isFeatureEnabled(
                            toFeatureState(toggles),
                            'nielsen-subbrand',
                        ),
                        gdprMode,
                    }
                    const showPlaylist = isFeatureEnabled(
                        toFeatureState(toggles),
                        'playlists-on-inline-videos',
                    )
                    const isAutoPlayNext = isFeatureEnabled(
                        toFeatureState(toggles),
                        'can-autoplay-next',
                    )
                    const autoplayOptions = false
                    const autoplayNextOptions =
                        isAutoPlayNext !== false &&
                        showPlaylist &&
                        getVideoQueue
                            ? {
                                  delayDuration: 3,
                              }
                            : false
                    return (
                        <ArticleVideo
                            captionText={captionText}
                            videoDetails={videoDetails}
                            showTitles={showInlineVideoTitles || false}
                            autoplayOptions={autoplayOptions}
                            autoplayNextOptions={autoplayNextOptions}
                            videoOptions={videoOptions}
                            pauseOverlay={false}
                            isVideoStickyEnabled={false}
                            onEvent={onEvent}
                            getVideoQueue={
                                showPlaylist && getVideoQueue
                                    ? getVideoQueue
                                    : () =>
                                          Promise.resolve<VideoQueue>({
                                              items: [],
                                              type: 'not-playlist-video',
                                          })
                            }
                            showPlaylist={showPlaylist}
                            publicationPathname={publicationPathname}
                        />
                    )
                }
                case 'image':
                    return (
                        <ArticleImage
                            align={
                                block.align === 'center'
                                    ? undefined
                                    : block.align
                            }
                            image={block.inlineDetails}
                            figCaption={{
                                byline: block.inlineDetails.byline || undefined,
                                captionText: block.inlineDetails.captionText,
                                credit: block.inlineDetails.credit || undefined,
                                kind: 'image',
                            }}
                            link={block.link}
                        />
                    )
                default:
                    return null
            }
        }
        case 'ordered-list': {
            return (
                <List
                    list={block}
                    log={log}
                    onEvent={onEvent}
                    adUnitPath={adUnitPath}
                    getVideoQueue={getVideoQueue}
                    publicationPathname={publicationPathname}
                    showInlineVideoTitles={showInlineVideoTitles}
                />
            )
        }
        case 'unordered-list': {
            return (
                <List
                    list={block}
                    log={log}
                    onEvent={onEvent}
                    adUnitPath={adUnitPath}
                    getVideoQueue={getVideoQueue}
                    publicationPathname={publicationPathname}
                    showInlineVideoTitles={showInlineVideoTitles}
                />
            )
        }
        case 'subhead': {
            return (
                <Subhead
                    intentions={block.intentions}
                    text={block.text}
                    tag="h2"
                    onEvent={onEvent}
                />
            )
        }
        case 'competition': {
            return (
                <Paragraph
                    onEvent={onEvent}
                    intentions={[
                        { kind: 'emphasized', index: 0, length: 27 },
                        { kind: 'important', index: 0, length: 27 },
                    ]}
                    text="Competition has now closed."
                />
            )
        }
        case 'inline-related': {
            return (
                <InlineRelatedContent
                    onEvent={onEvent}
                    items={block.publications}
                    isSevenEnt={publicationPathname?.includes(
                        '/entertainment/big-brother-australia/',
                    )}
                />
            )
        }
        default:
            return null
    }
}
